import type { ErrorCode, Problem, ProblemAction } from './types'

/**
 * Every failed request becomes an ApiError. `message` is the server's `detail`, a full sentence in
 * the DS voice that the UI shows verbatim; `nextStep` is the second line and `action` the button.
 */
export class ApiError extends Error {
  override readonly name = 'ApiError'
  /** HTTP status; 0 when the request never reached the server. */
  readonly status: number
  readonly code: ErrorCode | 'network_error' | 'unknown'
  readonly nextStep: string | null
  readonly action: ProblemAction | null
  readonly problem: Partial<Problem> | null

  constructor(init: {
    status: number
    code: ApiError['code']
    detail: string
    nextStep?: string | null
    action?: ProblemAction | null
    problem?: Partial<Problem> | null
  }) {
    super(init.detail)
    this.status = init.status
    this.code = init.code
    this.nextStep = init.nextStep ?? null
    this.action = init.action ?? null
    this.problem = init.problem ?? null
  }

  get isNetwork(): boolean {
    return this.status === 0
  }

  /** The detail and next step joined, for places that show one line. */
  get sentence(): string {
    return this.nextStep ? `${this.message} ${this.nextStep}` : this.message
  }

  /** Per-field messages from a 422 `validation_failed`, keyed by field name. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const f of this.problem?.fields ?? []) out[f.field] = f.message
    return out
  }
}

/** Copy for responses that carry no problem body (proxies, crashes), in the same voice. */
export function fallbackProblem(status: number): { detail: string; nextStep: string } {
  if (status === 401) return { detail: "You're signed out.", nextStep: 'Sign in to continue.' }
  if (status === 403) return { detail: "You don't have permission to do this.", nextStep: 'Ask an administrator.' }
  if (status === 404)
    return { detail: "This document doesn't exist or was deleted.", nextStep: 'Go back to Documents.' }
  if (status === 413)
    return { detail: 'This file is larger than 500 MB.', nextStep: 'Reduce or split the PDF, then upload it again.' }
  if (status === 429)
    return { detail: 'Too many requests from this computer.', nextStep: 'Wait a minute and try again.' }
  if (status === 502 || status === 503 || status === 504)
    return { detail: "The server isn't responding.", nextStep: 'Try again in a minute.' }
  return {
    detail: "The server couldn't complete this request.",
    nextStep: 'Try again. If it happens again, contact IT support.',
  }
}

export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let body: Partial<Problem> | null = null
  try {
    body = (await response.clone().json()) as Partial<Problem>
  } catch {
    body = null
  }
  const fallback = fallbackProblem(response.status)
  return new ApiError({
    status: response.status,
    code: body?.code ?? 'unknown',
    detail: body?.detail ?? fallback.detail,
    nextStep: body?.detail ? (body.nextStep ?? null) : fallback.nextStep,
    action: body?.action ?? null,
    problem: body,
  })
}

export function networkError(cause?: unknown): ApiError {
  const err = new ApiError({
    status: 0,
    code: 'network_error',
    detail: "Can't reach the server.",
    nextStep: 'Check your connection and try again.',
  })
  if (cause !== undefined) Object.defineProperty(err, 'cause', { value: cause })
  return err
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

/** One human sentence for any thrown value; never shows a stack or a status code. */
export function errorSentence(e: unknown): string {
  if (isApiError(e)) return e.sentence
  return 'Something went wrong. Try again. If it happens again, contact IT support.'
}
